import { DOCUMENT } from '@angular/common';
import { Component, Inject, Injector, ViewChild } from '@angular/core';
import { ProjectGuardService } from '@app/components/cleanui/system/Guard/project.guard';
import { SplitButtonElement } from '@app/components/elements/splitbutton/splitbutton.component';
import { participantPages } from '@app/pages/participant/participants.routes';
import { AbstractQuestionViewComponent } from '@app/pages/questions/abstract-question-view.component';
import { AfterViewCheckedObservable } from '@app/util/lifecycle-hooks/lifecycle-hooks';
import { firstValueFrom } from '@app/util/rxjs-7-compat';
import { INavTreeNode } from '@app/workflow/components/navigator/nav-tree/interfaces';
import { IQuestionnaireItem } from '@interfaces/interfaces';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { TWorkflowViewMode } from '@workflow/components/navigator/interfaces';
import { ModalDirective } from '@app/directives/modal/modal.directive';
import { WorkflowItemQueriesService } from '@workflow/queries/services/workflow-item-queries.service';

@Component({
	selector: 'workflow-compact-questionpage',
	templateUrl: './compact-view.component.html',
	styleUrls: ['./compact-view.component.scss'],
	providers: [
		ProjectGuardService, 
		WorkflowItemQueriesService, 
	],
})
export class CompactViewComponent extends AbstractQuestionViewComponent {

	public cancelWorkflowSplitbutton: SplitButtonElement = {
		title: 'workflow.cancel',
		type: 'default',
		icon: 'X',
		callback: () => this.showWorkflow(),
	};
	public overviewWorkflowSplitbutton: SplitButtonElement = {
		title: 'actions.show_workflow',
		type: 'default',
		icon: 'X',
		callback: () => this.showOverview(),
	};

	public resetWorkflowSplitbutton: SplitButtonElement = {
		title: 'workflow.reset',
		type: 'default',
		icon: 'trash',
		callback: () => this.resetWorkflow(),
	};

	@ViewChild('confirmReset')
	public resetConfirm: ModalDirective<boolean>;

	protected viewMode: TWorkflowViewMode = 'compact';

	prologPage: boolean[] = [];
	epilogPage: boolean[] = [];

	@AfterViewCheckedObservable
	private afterViewChecked: Observable<never>;

	constructor(
		injector: Injector,
		@Inject(DOCUMENT) private readonly document: Document
	) {
		super(injector);
	}

	isSpecialQuestion(question: IQuestionnaireItem): boolean {
		return this.individuelleFragetypen.includes(question?.type);
	}

	scrollToNode(node: INavTreeNode): void {
		while (node) {
			if (node.isQuestionGroup) {
				const urlPrefix = node.urlPrefixes[0];
				if (urlPrefix) {
					const anchor = 'p' + urlPrefix.replace('/', '_');
					this.goToAnchor(anchor);
				}
				break;
			}
			node = node.parent;
		}
	}

	private async goToAnchor(anchor: string): Promise<void> {
		if (anchor) {
			/**
			 * Wenn der Status noch nicht aktualisiert wurde (also finishedAndLocked noch nicht gesetzt ist),
			 * werden keinen Fragen gerendert. Damit sind auch kein Anchors verfügbar. Der folgende Code wartet
			 * darauf, dass statusSet einen Wert gesetzt bekommt. Danach wird noch der ViewCheck abgewartet,
			 * um den DOM-Baum zu aktualisieren.
			 */
			const viewReady = this.statusSet.pipe(
				switchMap(() => this.afterViewChecked),
			);
			await firstValueFrom(viewReady);
			
			const element = this.document.querySelector(`#${anchor}`);
			
			element.scrollIntoView({
				behavior: 'auto',
				block: 'start'
			});
		}
	}

	togglePrologue(index: number): void {
		this.prologPage[index] = !this.prologPage[index];
	}

	toggleEpilog(index: number): void {
		this.epilogPage[index] = !this.epilogPage[index];
	}

	wizardLink(groupId: number, contentId: number): string {
		return participantPages.workflowProcessContentItem.base({ wfItemId: this.workflowItem.id, questionGroupId: groupId, contentId: contentId });
	}

	private async resetWorkflow(): Promise<void> {
		if (await this.resetConfirm.show()) {
			await this.workflowService.resetWorkflowItemStatus(this.workflowItem.id);
			this.showOverview();
		}
	}
}
